(() => {
  if (customElements.get('presale-page')) return;
  // Media events can arrive after an editor detaches/reconnects the same element.
  // Keep pause intent with that video, independently of the section's event signal.
  const trackingPlayback = new WeakMap();
  const trackingState = video => {
    let state = trackingPlayback.get(video);
    if (state) return state;
    state = { manuallyPaused: false, automaticPauses: 0, mayResume: true };
    trackingPlayback.set(video, state);
    video.addEventListener('pause', () => {
      if (state.automaticPauses) { state.automaticPauses--; return; }
      state.manuallyPaused = true;
      state.mayResume = false;
    });
    video.addEventListener('play', () => {
      // Ignore an already superseded play event that was queued before a pause.
      if (video.paused) return;
      state.manuallyPaused = false;
      state.mayResume = false;
      delete video.dataset.autoplayBlocked;
    });
    video.addEventListener('ended', () => {
      if (!video.loop) { state.manuallyPaused = true; state.mayResume = false; }
    });
    return state;
  };
  const pauseTrackingVideo = video => {
    if (!video || video.paused) return;
    const state = trackingState(video);
    state.automaticPauses++;
    state.manuallyPaused = false;
    state.mayResume = true;
    video.pause();
  };
  class PresalePage extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.storyPreloader?.disconnect();
      this.trackingVideoObserver?.disconnect();
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.storyJumpFrame);
      this.scrollFrame = null;
      this.storyJumpFrame = null;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.layoutDirty = true;
      this.layout = null;
      this.storyProgress = null;
      this.nav = this.querySelector('.presale__nav');
      this.navLinks = this.nav?.querySelector('.presale__nav-links');
      this.activeNavigation = this.nav?.querySelector('[aria-current]') || null;
      this.navigation = [...this.querySelectorAll('.presale__nav-links a')].map(link => ({ link, target: this.querySelector(link.getAttribute('href')) }));
      this.container = this.querySelector('.presale__container');
      this.scheduleUpdate = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateScroll();
        });
      };
      this.invalidateLayout = () => { this.layoutDirty = true; this.scheduleUpdate(); };
      clearInterval(this.timer);
      this.timer = null;
      if (this.querySelector('[data-presale-time], [data-presale-reserve]')) {
        this.deadline = Date.parse(this.dataset.launchAt);
        this.serverNow = Number(this.dataset.serverNow) * 1000;
        this.startedAt = Date.now();
        this.tick();
        this.timer = setInterval(() => this.tick(), 1000);
      }
      this.querySelector('[data-presale-reserve]')?.addEventListener('submit', event => this.reserve(event), { signal });
      const dialog = this.querySelector('[data-presale-terms]');
      this.querySelector('[data-presale-open-terms]')?.addEventListener('click', () => dialog?.showModal(), { signal });
      this.querySelector('[data-presale-close-terms]')?.addEventListener('click', () => dialog?.close(), { signal });
      dialog?.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
      }, { signal });
      this.querySelectorAll('.presale__nav a[href^="#"]').forEach(link => link.addEventListener('click', event => {
        const target = this.querySelector(link.getAttribute('href'));
        if (!target) return;
        event.preventDefault();
        cancelAnimationFrame(this.storyJumpFrame);
        this.storyJumpFrame = null;
        this.revealNavigation(link);
        const storyTarget = target.classList.contains('presale__story-stop');
        if (storyTarget) this.updateScroll();
        target.scrollIntoView({ behavior: storyTarget || matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
        // Paint the title at its destination in this event, without replaying the intervening scenes.
        if (storyTarget) this.updateScroll();
        // Give keyboard users a useful destination after an anchor jump.
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
        if (storyTarget) {
          const landingY = window.scrollY;
          // The theme header may show/hide on the jump's scroll event. Correct once after it,
          // and abandon the correction if the visitor has already resumed scrolling.
          this.storyJumpFrame = requestAnimationFrame(() => {
            this.storyJumpFrame = null;
            if (Math.abs(window.scrollY - landingY) > 1) return;
            this.updateScroll();
            target.scrollIntoView({ behavior: 'instant', block: 'start' });
            this.updateScroll();
          });
        }
      }, { signal }));
      this.navLinks?.addEventListener('focusin', event => {
        const link = event.target.closest('a');
        if (link) this.revealNavigation(link);
      }, { signal });
      this.setupStoryMotion(signal);
      this.setupHomeMotion(signal);
      this.setupComparisons(signal);
      this.setupTrackingVideo(signal);
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.invalidateLayout);
        const nodes = [this, this.container, this.nav, ...Object.values(this.storyNodes || {}), this.home, this.homeNodes?.stage];
        new Set(nodes.filter(node => node instanceof HTMLElement)).forEach(node => this.resizeObserver.observe(node));
      }
      this.addEventListener('load', this.invalidateLayout, { signal, capture: true });
      this.addEventListener('toggle', this.invalidateLayout, { signal, capture: true });
      document.fonts?.ready.then(() => { if (!signal.aborted) this.invalidateLayout(); });
      window.addEventListener('scroll', this.scheduleUpdate, { signal, passive: true });
      window.addEventListener('resize', this.invalidateLayout, { signal, passive: true });
      window.addEventListener('pageshow', this.invalidateLayout, { signal });
      this.updateScroll();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.requestController?.abort();
      clearInterval(this.timer);
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.storyJumpFrame);
      this.scrollFrame = null;
      this.storyJumpFrame = null;
      this.resizeObserver?.disconnect();
      this.storyPreloader?.disconnect();
      this.trackingVideoObserver?.disconnect();
      pauseTrackingVideo(this.trackingVideo);
      this.story?.removeAttribute('data-presale-motion');
      this.story?.removeAttribute('data-presale-in-view');
      this.home?.removeAttribute('data-presale-home-motion');
      this.home?.removeAttribute('data-presale-home-in-view');
      this.homeNodes?.shade?.style.removeProperty('--presale-home-shade');
      this.homeNodes?.copy?.style.removeProperty('--presale-home-copy-opacity');
      this.homeNodes?.copy?.style.removeProperty('--presale-home-copy-y');
      this.comparisons?.forEach(comparison => {
        cancelAnimationFrame(comparison.frame);
        if (comparison.pointerId != null && comparison.element.hasPointerCapture(comparison.pointerId)) comparison.element.releasePointerCapture(comparison.pointerId);
        comparison.element.removeAttribute('data-presale-comparison-ready');
        comparison.control.hidden = true;
      });
      this.querySelector('[data-presale-terms]')?.close();
    }
    revealNavigation(link) {
      const rail = this.navLinks;
      if (!rail?.contains(link) || innerWidth > 749) return;
      const railBounds = rail.getBoundingClientRect(), linkBounds = link.getBoundingClientRect();
      const distance = linkBounds.left < railBounds.left + 8 ? linkBounds.left - railBounds.left - 8
        : linkBounds.right > railBounds.right - 8 ? linkBounds.right - railBounds.right + 8 : 0;
      if (Math.abs(distance) < 1) return;
      const left = Math.min(rail.scrollWidth - rail.clientWidth, Math.max(0, rail.scrollLeft + distance));
      // Move only the horizontal strip; scrollIntoView would also move the page.
      rail.scrollTo({ left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }
    setupTrackingVideo(signal) {
      const video = this.querySelector('[data-presale-tracking-video]');
      if (this.trackingVideo && this.trackingVideo !== video) pauseTrackingVideo(this.trackingVideo);
      this.trackingVideo = video;
      if (!video) return;
      const state = trackingState(video);
      if (video.dataset.videoAutoplay !== 'true' || !('IntersectionObserver' in window)) return;
      const preference = matchMedia('(prefers-reduced-motion: reduce)');
      let visible = false, attempt = 0;
      const sync = () => {
        if (signal.aborted || !video.isConnected) return;
        if (!visible || preference.matches || document.hidden) {
          attempt++;
          pauseTrackingVideo(video);
          return;
        }
        if (state.manuallyPaused || !state.mayResume || !video.paused) return;
        state.mayResume = false;
        const current = ++attempt;
        video.play().catch(() => {
          // Keep the poster visible if the browser rejects muted background playback.
          if (!signal.aborted && current === attempt) video.dataset.autoplayBlocked = 'true';
        });
      };
      preference.addEventListener('change', sync, { signal });
      document.addEventListener('visibilitychange', sync, { signal });
      this.trackingVideoObserver = new IntersectionObserver(entries => {
        const entry = entries.find(value => value.target === video);
        if (!entry) return;
        visible = entry.isIntersecting && entry.intersectionRatio >= .1;
        sync();
      }, { threshold: [0, .1] });
      this.trackingVideoObserver.observe(video);
    }
    setupComparisons(signal) {
      this.comparisons = [...this.querySelectorAll('[data-presale-comparison]')].map(element => {
        const control = element.querySelector('[data-presale-comparison-control]');
        const comparison = { element, control, frame: null, pointerId: null, bounds: null };
        const render = () => {
          cancelAnimationFrame(comparison.frame);
          comparison.frame = null;
          const value = Math.min(100, Math.max(0, Number(control.value) || 0));
          const position = `${value}%`;
          if (element.style.getPropertyValue('--presale-compare-position') !== position) element.style.setProperty('--presale-compare-position', position);
          control.setAttribute('aria-valuetext', `${element.dataset.beforeLabel} ${value}% · ${element.dataset.afterLabel} ${100 - value}%`);
        };
        const move = event => {
          const bounds = comparison.bounds ||= element.getBoundingClientRect();
          if (!bounds.width) return;
          control.value = String(Math.round(Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width)) * 100));
          if (!comparison.frame) comparison.frame = requestAnimationFrame(render);
        };
        const end = event => {
          if (event.pointerId !== comparison.pointerId) return;
          if (event.type === 'pointerup') move(event);
          render();
          comparison.pointerId = null;
          if (element.hasPointerCapture(event.pointerId)) element.releasePointerCapture(event.pointerId);
        };
        control.hidden = false;
        element.setAttribute('data-presale-comparison-ready', '');
        render();
        control.addEventListener('input', render, { signal });
        element.addEventListener('pointerdown', event => {
          if (!event.isPrimary || event.button !== 0 || comparison.pointerId != null) return;
          comparison.pointerId = event.pointerId;
          comparison.bounds = element.getBoundingClientRect();
          control.focus({ preventScroll: true });
          element.setPointerCapture(event.pointerId);
          move(event);
        }, { signal });
        element.addEventListener('pointermove', event => { if (event.pointerId === comparison.pointerId) move(event); }, { signal });
        for (const name of ['pointerup', 'pointercancel', 'lostpointercapture']) element.addEventListener(name, end, { signal });
        window.addEventListener('resize', () => { comparison.bounds = null; }, { signal, passive: true });
        return comparison;
      });
    }
    setupHomeMotion(signal) {
      this.home = this.querySelector('[data-presale-home]');
      this.homeNodes = null;
      if (!this.home) return;
      this.homeNodes = {
        stage: this.home.querySelector('.presale__home-stage'),
        shade: this.home.querySelector('.presale__home-shade'),
        copy: this.home.querySelector('.presale__banner-copy'),
        media: this.home.querySelector('.presale__banner-media img, .presale__banner-media video'),
      };
      this.homePreference = matchMedia('(prefers-reduced-motion: reduce), (max-height: 499px)');
      this.syncHomeMotion = () => {
        const enabled = this.dataset.presaleMotionEnabled === 'true' && this.home.dataset.homeMotionEnabled === 'true'
          && !!this.homeNodes.media && !this.homePreference.matches
          && CSS.supports('position', 'sticky') && CSS.supports('height', '100svh');
        this.home.toggleAttribute('data-presale-home-motion', enabled);
        this.homeProgress = null;
        if (!enabled) {
          this.home.removeAttribute('data-presale-home-in-view');
          this.homeNodes.shade?.style.removeProperty('--presale-home-shade');
          this.homeNodes.copy?.style.removeProperty('--presale-home-copy-opacity');
          this.homeNodes.copy?.style.removeProperty('--presale-home-copy-y');
        }
        this.invalidateLayout();
      };
      this.homePreference.addEventListener('change', this.syncHomeMotion, { signal });
      this.syncHomeMotion();
    }
    setupStoryMotion(signal) {
      this.story = this.querySelector('[data-presale-story]');
      this.storyNodes = null;
      if (!this.story) return;
      this.storyNodes = {};
      for (const [name, selector] of Object.entries({ stage: '.presale__story-stage', frame: '.presale__ultra-inner', statement: '.presale__statement', lineOne: '.presale__statement span', lineTwo: '.presale__statement span + span', ultra: '.presale__ultra', image: '.presale__lens img', lens: '.presale__lens', mask: '.presale__focus-mask', glow: '.presale__lens-glow', caption: '.presale__ultra-copy', secondary: '.presale__detail-copy', tertiary: '.presale__light-copy', heading: '.presale__ultra-copy h2', leader: '.presale__focus-leader', secondaryLeader: '.presale__focus-leader--secondary', tertiaryLeader: '.presale__focus-leader--tertiary' })) {
        this.storyNodes[name] = this.story.querySelector(selector);
      }
      this.leaderSegments = [...this.story.querySelectorAll('[data-presale-leader-segment]')];
      this.secondarySegments = [...this.story.querySelectorAll('[data-presale-secondary-segment]')];
      this.tertiarySegments = [...this.story.querySelectorAll('[data-presale-tertiary-segment]')];
      const annotationOrder = this.story.dataset.annotationOrder === 'primary_tertiary_secondary' ? [0, 2, 1] : [0, 1, 2];
      this.storySequence = annotationOrder.filter(index => [this.storyNodes.caption, this.storyNodes.secondary, this.storyNodes.tertiary][index]);
      this.storyNodes.headline = this.story.querySelector('.presale__statement h2');
      // Keep ordinary document scrolling. Sticky layout supplies the hold; no wheel/touch interception.
      this.motionPreference = matchMedia('(prefers-reduced-motion: reduce), (max-height: 599px)');
      this.syncStoryMotion = () => {
        const enabled = this.dataset.presaleMotionEnabled === 'true' && !!this.storyNodes.image && !this.motionPreference.matches
          && CSS.supports('position', 'sticky') && CSS.supports('height', '100svh') && CSS.supports('selector(:has(*))');
        this.story.toggleAttribute('data-presale-motion', enabled);
        if (!enabled) this.story.removeAttribute('data-presale-in-view');
        this.storyProgress = null;
        this.invalidateLayout();
      };
      this.motionPreference.addEventListener('change', this.syncStoryMotion, { signal });
      this.syncStoryMotion();
      const { image } = this.storyNodes;
      if ('IntersectionObserver' in window && image) {
        this.storyPreloader = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          image.loading = 'eager';
          this.storyPreloader.disconnect();
        }, { rootMargin: '150% 0px' });
        this.storyPreloader.observe(this.story);
      }
    }
    measureLayout(scrollY, headerOffset, sectionTop) {
      const nodes = this.storyNodes;
      const bounds = this.story?.getBoundingClientRect();
      const homeBounds = this.home?.getBoundingClientRect();
      const layout = {
        headerOffset, sectionTop, navHeight: this.nav?.offsetHeight || 0,
        svgScale: (this.container?.clientWidth || 1280) / 1280,
        targets: this.navigation.map(({ target }) => target ? target.getBoundingClientRect().top + scrollY : Infinity),
        storyTop: bounds ? bounds.top + scrollY : 0, storyHeight: bounds?.height || 0,
        stageHeight: nodes?.stage?.clientHeight || 0, viewportHeight: innerHeight,
        mobile: innerWidth <= 749,
        artboardScale: innerWidth <= 749 ? 1 : Math.min((nodes?.stage?.clientWidth || 1920) / 1920, (nodes?.stage?.clientHeight || 900) / 900),
        homeTop: homeBounds ? homeBounds.top + scrollY : 0,
        homeHeight: homeBounds?.height || 0, homeStageHeight: this.homeNodes?.stage?.clientHeight || 0,
      };
      if (nodes?.headline && this.story.hasAttribute('data-presale-motion')) {
        const growth = Math.min(2, Math.max(1, Number(this.story.dataset.titleZoom) || 1.4));
        const textWidth = Math.max(nodes.lineOne?.offsetWidth || 1, nodes.lineTwo?.offsetWidth || 1);
        const availableWidth = nodes.stage.clientWidth - (layout.mobile ? 40 : 64);
        const maximum = Math.max(.1, Math.min(growth, availableWidth / textWidth, (layout.stageHeight - 64) / Math.max(1, nodes.headline.offsetHeight)));
        // Measure untransformed text once. Scaling stays on a leaf compositor layer while scrolling.
        layout.titleStart = maximum / growth;
        layout.titleEnd = maximum;
      }
      if (nodes?.image && nodes.lens && nodes.caption && nodes.frame && this.story.hasAttribute('data-presale-motion')) {
        const { image, lens, frame } = nodes;
        const imageStyle = getComputedStyle(image);
        const imageWidth = image.clientWidth, imageHeight = image.clientHeight;
        if (imageWidth && imageHeight) {
          const read = (key, fallback, min, max) => {
            const value = Number(this.story.dataset[key]);
            return Math.min(max, Math.max(min, Number.isFinite(value) ? value : fallback));
          };
          const naturalWidth = image.naturalWidth || imageWidth, naturalHeight = image.naturalHeight || imageHeight;
          const fit = Math.min(imageWidth / naturalWidth, imageHeight / naturalHeight);
          const width = imageStyle.objectFit === 'contain' ? naturalWidth * fit : imageWidth;
          const height = imageStyle.objectFit === 'contain' ? naturalHeight * fit : imageHeight;
          const frameScale = frame.getBoundingClientRect().width / frame.clientWidth || 1;
          const imageLeft = (parseFloat(imageStyle.left) || 0) + (imageWidth - width) / 2;
          const imageTop = (parseFloat(imageStyle.top) || 0) + (imageHeight - height) / 2;
          layout.focus = {
            dim: read('focusDim', 90, 0, 100) / 100,
            lensWidth: lens.clientWidth, lensHeight: lens.clientHeight, lensLeft: lens.offsetLeft, lensTop: lens.offsetTop,
            stageWidth: frame.clientWidth, stageHeight: frame.clientHeight,
            visibleLeft: -(nodes.stage.clientWidth / frameScale - frame.clientWidth) / 2,
            visibleTop: -(nodes.stage.clientHeight / frameScale - frame.clientHeight) / 2,
            visibleWidth: nodes.stage.clientWidth / frameScale, visibleHeight: nodes.stage.clientHeight / frameScale,
            imageTop, imageCenterX: imageLeft + width / 2,
            lensCenterX: imageLeft + width * read('secondaryFocusX', 50, 0, 100) / 100,
            lensCenterY: imageTop + height * read('secondaryFocusY', 66, 0, 100) / 100,
            components: [nodes.caption, nodes.secondary, nodes.tertiary].map((caption, index) => {
              if (!caption) return null;
              const heading = caption.querySelector('h2');
              const prefix = ['focus', 'secondaryFocus', 'tertiaryFocus'][index];
              return {
                localX: imageLeft + width * read(`${prefix}X`, index === 2 ? 41 : 50, 0, 100) / 100,
                localY: imageTop + height * read(`${prefix}Y`, [37, 66, 61][index], 0, 100) / 100,
                radius: width * read(`${prefix}Radius`, index === 2 ? 6 : 9, 3, 20) / 100,
                edgeRadius: width * read(`${prefix}EdgeRadius`, [4.7, 5.8, 2.7][index], .5, 10) / 100,
                headingHeight: heading.offsetHeight,
                captionHeight: caption.offsetHeight, captionWidth: caption.offsetWidth, captionLeft: caption.offsetLeft,
                captionTop: Math.max(24, Math.min(layout.mobile ? frame.clientHeight * .6136 : (index ? 491 : 272), frame.clientHeight - caption.offsetHeight - 24)),
              };
            }),
          };
          if (layout.mobile) {
            const captions = layout.focus.components.filter(Boolean);
            const captionHeight = Math.max(0, ...captions.map(caption => caption.captionHeight));
            const scale = this.story.hasAttribute('data-presale-focus-enabled') ? read('focusZoom', 1.2, 1, 1.8) : 1;
            const cameraBottom = (Math.min(lens.clientHeight, imageTop + height) - imageTop) * scale;
            const top = Math.max(24, Math.min(Math.max(frame.clientHeight * .6136, cameraBottom + 32), frame.clientHeight - captionHeight - 24));
            captions.forEach(caption => { caption.captionTop = top; });
          }
        }
      }
      this.layout = layout;
      this.layoutDirty = false;
      return layout;
    }
    updateScroll() {
      // Read layout before touching navigation, CSS variables or SVG. Fixed geometry is measured
      // only after resize/content changes; ordinary scroll frames reuse it.
      const scrollY = window.scrollY;
      const headerOffset = parseFloat(getComputedStyle(this).getPropertyValue('--presale-sticky-offset')) || 0;
      const sectionTop = this.getBoundingClientRect().top + scrollY;
      const changed = this.layoutDirty || !this.layout || this.layout.headerOffset !== headerOffset || Math.abs(this.layout.sectionTop - sectionTop) > .5;
      const layout = changed ? this.measureLayout(scrollY, headerOffset, sectionTop) : this.layout;
      const threshold = scrollY + Math.max((this.nav?.getBoundingClientRect().bottom || 0) + 24, innerHeight * .25);
      let active = this.navigation[0]?.link;
      this.navigation.forEach(({ link }, index) => { if (layout.targets[index] <= threshold) active = link; });
      // Everything below is write-only. In particular, never measure after changing aria-current.
      const svgScale = String(layout.svgScale);
      if (this.style.getPropertyValue('--presale-svg-scale') !== svgScale) this.style.setProperty('--presale-svg-scale', svgScale);
      if (this.dataset.presaleProductDetails === 'true' && this.nav) this.setMotionValues(this, { '--presale-anchor-gap': `${layout.navHeight + 18}px` });
      if (this.story) this.setMotionValues(this.story, { '--presale-story-nav': `${layout.navHeight}px` });
      this.updateStoryMotion(layout, scrollY, changed);
      this.updateHomeMotion(layout, scrollY, changed);
      if (active !== this.activeNavigation) {
        this.activeNavigation?.removeAttribute('aria-current');
        active?.setAttribute('aria-current', 'location');
        this.activeNavigation = active;
      }
    }
    updateHomeMotion(layout, scrollY, changed) {
      if (!this.home?.hasAttribute('data-presale-home-motion')) return;
      const progress = Math.min(1, Math.max(0, (scrollY + layout.headerOffset + layout.navHeight - layout.homeTop) / Math.max(1, layout.homeHeight - layout.homeStageHeight)));
      const inView = layout.homeTop - scrollY < layout.viewportHeight * 1.25 && layout.homeTop + layout.homeHeight - scrollY > 0;
      if (this.home.hasAttribute('data-presale-home-in-view') !== inView) this.home.toggleAttribute('data-presale-home-in-view', inView);
      if (this.homeProgress === progress && !changed) return;
      this.homeProgress = progress;
      const ease = value => value * value * (3 - 2 * value);
      const range = (start, end) => ease(Math.min(1, Math.max(0, (progress - start) / (end - start))));
      const maximum = Number(this.home.dataset.homeMaxShade);
      const darkness = Math.min(.8, Math.max(0, Number.isFinite(maximum) ? maximum / 100 : .6));
      this.setMotionValues(this.home, { '--presale-home-nav': `${layout.navHeight}px` });
      this.setMotionValues(this.homeNodes.shade, { '--presale-home-shade': darkness * range(.08, .42) });
      const copy = range(.42, .68);
      this.setMotionValues(this.homeNodes.copy, { '--presale-home-copy-opacity': copy, '--presale-home-copy-y': `${24 * (1 - copy)}px` });
    }
    updateStoryMotion(layout, scrollY, changed) {
      if (!this.story?.hasAttribute('data-presale-motion')) return;
      const { navHeight, headerOffset } = layout;
      const top = headerOffset + navHeight;
      const travel = Math.max(1, layout.storyHeight - layout.stageHeight);
      const clamp = value => Math.min(1, Math.max(0, value));
      const range = (value, start, end) => clamp((value - start) / (end - start));
      const ease = value => value * value * (3 - 2 * value);
      const progress = clamp((top - layout.storyTop + scrollY) / travel);
      const inView = layout.storyTop - scrollY < layout.viewportHeight * 1.25 && layout.storyTop + layout.storyHeight - scrollY > 0;
      if (this.story.hasAttribute('data-presale-in-view') !== inView) this.story.toggleAttribute('data-presale-in-view', inView);
      if (progress === this.storyProgress && !changed) return;
      this.storyProgress = progress;
      const zoom = ease(range(progress, .44, .58));
      const startingZoom = Math.min(5, Math.max(1, Number(this.dataset.presaleZoom) || 3.8));
      const sequence = this.storySequence;
      const opacity = [0, 0, 0];
      const entrances = [[.59, .65], [.73, .77], [.87, .92]];
      const exits = [[.69, .73], [.83, .87]];
      sequence.forEach((component, position) => {
        opacity[component] = ease(range(progress, ...entrances[position])) * (position < sequence.length - 1 ? 1 - ease(range(progress, ...exits[position])) : 1);
      });
      const [copy, secondCopy, tertiary] = opacity;
      const focusEnabled = this.story.hasAttribute('data-presale-focus-enabled');
      const focus = focusEnabled ? ease(range(progress, .58, .64)) : 0;
      const focusZoom = Math.min(1.8, Math.max(1, Number(this.story.dataset.focusZoom) || 1.2));
      const cameraScale = startingZoom + ((focusEnabled ? focusZoom : 1) - startingZoom) * zoom;
      const switchComponent = ease(range(progress, .69, .77));
      const switchLast = sequence.length > 2 ? ease(range(progress, .83, .92)) : 0;
      const camera = this.getCameraPosition(layout, cameraScale, zoom);
      const detail = this.getFocusGeometry(layout, cameraScale, 0, 12 * (1 - copy), camera);
      const secondDetail = this.getFocusGeometry(layout, cameraScale, 1, 12 * (1 - secondCopy), camera);
      const thirdDetail = this.getFocusGeometry(layout, cameraScale, 2, 12 * (1 - tertiary), camera);
      const nodes = this.storyNodes;
      // Scope changing variables to their actual consumers. Inheriting every frame's values
      // from the story root unnecessarily invalidates all text, pictures and leader segments.
      this.setMotionValues(nodes.frame, { '--presale-artboard-scale': layout.artboardScale });
      this.setMotionValues(nodes.headline, { '--presale-title-scale': (layout.titleStart ?? 1) + ((layout.titleEnd ?? 1) - (layout.titleStart ?? 1)) * ease(range(progress, .04, .30)) });
      this.setMotionValues(nodes.statement, {
        '--presale-title-opacity': 1 - ease(range(progress, .34, .40)),
        '--presale-title-y': `${-24 * ease(range(progress, .34, .40))}px`,
      });
      this.setMotionValues(nodes.ultra, { '--presale-camera-opacity': ease(range(progress, .36, .42)) });
      this.setMotionValues(nodes.lens, {
        '--presale-camera-scale': cameraScale,
        '--presale-camera-x': `${camera.x}px`, '--presale-camera-y': `${camera.y}px`,
      });
      this.setMotionValues(nodes.caption, {
        '--presale-copy-opacity': copy,
        '--presale-copy-y': `${12 * (1 - copy)}px`,
        '--presale-caption-top': `${detail?.captionTop ?? (layout.mobile ? 24 : 272)}px`,
      });
      this.setMotionValues(nodes.secondary, { '--presale-copy-opacity': secondCopy, '--presale-copy-y': `${12 * (1 - secondCopy)}px`, '--presale-caption-top': `${secondDetail?.captionTop ?? 491}px` });
      this.setMotionValues(nodes.tertiary, { '--presale-copy-opacity': tertiary, '--presale-copy-y': `${12 * (1 - tertiary)}px`, '--presale-caption-top': `${thirdDetail?.captionTop ?? 491}px` });
      this.setMotionValues(nodes.glow, { '--presale-glow-opacity': 0 });
      if (detail) {
        const details = [detail, secondDetail, thirdDetail];
        const active = details[sequence[1]] || detail;
        const last = details[sequence[2]] || active;
        const mix = name => {
          const value = detail[name] + (active[name] - detail[name]) * switchComponent;
          return value + (last[name] - value) * switchLast;
        };
        this.setMotionValues(nodes.mask, { '--presale-focus-progress': focus, '--presale-focus-x': `${mix('localX')}px`, '--presale-focus-y': `${mix('localY')}px`, '--presale-focus-radius': `${mix('radius')}px`, '--presale-focus-dim': layout.focus.dim });
      }
      this.setMotionValues(nodes.leader, { '--presale-focus-progress': copy });
      this.setMotionValues(nodes.secondaryLeader, { '--presale-focus-progress': secondCopy });
      this.setMotionValues(nodes.tertiaryLeader, { '--presale-focus-progress': tertiary });
      if (!layout.mobile && this.story.hasAttribute('data-presale-leader-enabled')) {
        if (detail && copy > 0) this.drawFocusLeader(detail, copy, 0);
        if (secondDetail && secondCopy > 0) this.drawFocusLeader(secondDetail, secondCopy, 1);
        if (thirdDetail && tertiary > 0) this.drawFocusLeader(thirdDetail, tertiary, 2);
      }
    }
    setMotionValues(node, values) {
      if (!node) return;
      Object.entries(values).forEach(([name, value]) => {
        value = String(value);
        if (node.style.getPropertyValue(name) !== value) node.style.setProperty(name, value);
      });
    }
    drawFocusLeader(detail, progress, index = 0) {
      const leader = [this.storyNodes.leader, this.storyNodes.secondaryLeader, this.storyNodes.tertiaryLeader][index];
      const set = (node, name, value) => { if (node && node.style[name] !== value) node.style[name] = value; };
      set(leader, 'visibility', detail.leaderVisible ? 'visible' : 'hidden');
      // Keep the horizontal line at its actual width; only scale its reveal from 0 to 1.
      const segments = detail.points.slice(1).map(([x, y], index) => {
        const [startX, startY] = detail.points[index];
        return { startX, startY, length: Math.hypot(x - startX, y - startY), angle: Math.atan2(y - startY, x - startX) };
      });
      let remaining = segments.reduce((sum, segment) => sum + segment.length, 0) * progress;
      [this.leaderSegments, this.secondarySegments, this.tertiarySegments][index].forEach((node, index) => {
        const segment = segments[index];
        const length = segment ? Math.min(segment.length, Math.max(0, remaining)) : 0;
        if (segment) remaining -= segment.length;
        set(node, 'width', `${segment?.length || 0}px`);
        set(node, 'transform', segment ? `translate3d(${segment.startX}px, ${segment.startY - .5}px, 0) rotate(${segment.angle}rad) scaleX(${segment.length ? length / segment.length : 0})` : 'scaleX(0)');
      });
    }
    getCameraPosition(layout, scale, zoom) {
      if (!layout.focus) return { x: 0, y: 0 };
      const { lensLeft, lensTop, lensWidth, lensHeight, stageWidth, stageHeight, imageCenterX, imageTop, lensCenterX, lensCenterY, visibleTop } = layout.focus;
      const centerX = lensLeft + lensWidth / 2, centerY = lensTop + lensHeight / 2;
      const closeX = stageWidth / 2 - centerX - (lensCenterX - lensWidth / 2) * scale;
      const closeY = stageHeight / 2 - centerY - (lensCenterY - lensHeight / 2) * scale;
      const wideX = stageWidth / 2 - centerX - (imageCenterX - lensWidth / 2) * scale;
      const wideY = visibleTop - centerY - (imageTop - lensHeight / 2) * scale;
      return { x: closeX + (wideX - closeX) * zoom, y: closeY + (wideY - closeY) * zoom };
    }
    getFocusGeometry(layout, scale, index, copyY, camera = { x: 0, y: 0 }) {
      if (!layout.focus?.components[index]) return null;
      const { lensWidth, lensHeight, lensLeft, lensTop, visibleLeft, visibleTop, visibleWidth, visibleHeight } = layout.focus;
      const { localX, localY, radius, edgeRadius, headingHeight, captionHeight, captionWidth, captionLeft, captionTop: authoredTop } = layout.focus.components[index];
      const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
      // Project the CURRENT frame's transform rather than reading last frame's transformed bounds.
      const targetX = lensLeft + lensWidth * .5 + (localX - lensWidth * .5) * scale + camera.x;
      const targetY = lensTop + lensHeight * .5 + (localY - lensHeight * .5) * scale + camera.y;
      const { mobile } = layout;
      const right = index === 1;
      // Align the rule with the component, keeping the whole caption inside the pinned viewport.
      // Mobile annotations retain their shared position below the product and have no leaders.
      const captionTop = mobile ? authoredTop : clamp(targetY - headingHeight, visibleTop + 24, visibleTop + visibleHeight - captionHeight - 24);
      const startX = right ? captionLeft + captionWidth : captionLeft;
      const startY = captionTop + headingHeight + copyY;
      const projectedRadius = edgeRadius * scale;
      const verticalDistance = startY - targetY;
      const intersectsEdge = Math.abs(verticalDistance) <= projectedRadius;
      const horizontalDistance = Math.sqrt(Math.max(0, projectedRadius * projectedRadius - verticalDistance * verticalDistance));
      const endX = targetX + (right ? horizontalDistance : -horizontalDistance);
      const endY = startY;
      const points = [[startX, startY], [endX, endY]];
      const componentVisible = localX + radius > 0 && localX - radius < lensWidth && localY + radius > 0 && localY - radius < lensHeight
        && targetX + radius * scale > visibleLeft && targetX - radius * scale < visibleLeft + visibleWidth
        && targetY + radius * scale > visibleTop && targetY - radius * scale < visibleTop + visibleHeight;
      return {
        endX, endY, points, localX, localY, radius, captionTop,
        // A clamped/offscreen replacement component must not get a line pointing to empty space.
        leaderVisible: !mobile && componentVisible && intersectsEdge && endX >= visibleLeft && endX <= visibleLeft + visibleWidth
          && (right ? endX < startX - 16 : endX > startX + 16),
      };
    }
    remaining() {
      const now = this.serverNow > 0 ? this.serverNow + Date.now() - this.startedAt : Date.now();
      return Number.isFinite(this.deadline) ? Math.max(0, Math.floor((this.deadline - now) / 1000)) : 0;
    }
    tick() {
      const seconds = this.remaining();
      const units = [Math.floor(seconds / 86400), Math.floor(seconds / 3600) % 24, Math.floor(seconds / 60) % 60, seconds % 60];
      this.querySelectorAll('[data-presale-time]').forEach((node, index) => {
        const value = String(units[index]).padStart(2, '0');
        if (node.textContent !== value) node.textContent = value;
      });
      if (!seconds) {
        const button = this.querySelector('[data-presale-reserve] button');
        const status = this.querySelector('[data-presale-reservation-status]');
        if (button) button.disabled = true;
        if (status) status.textContent = this.dataset.presaleClosedText || "Reservations have closed.";
      }
    }
    async reserve(event) {
      event.preventDefault();
      const form = event.currentTarget;
      const button = form.querySelector('button');
      if (button.disabled || this.busy || !this.remaining()) return;
      this.busy = true;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      const status = this.querySelector('[data-presale-reservation-status]');
      status.hidden = false;
      status.textContent = this.dataset.presaleLoadingText || "Adding your reservation…";
      const body = new FormData(form);
      this.requestController = new AbortController();
      const timeout = setTimeout(() => this.requestController.abort(), 15000);
      try {
        const response = await fetch(`${form.action}.js`, { method: 'POST', headers: { Accept: 'application/json' }, body, signal: this.requestController.signal });
        const result = await response.json();
        if (!response.ok || result.status >= 400 || !result.id) throw new Error(typeof result.description === 'string' ? result.description : (this.dataset.presaleErrorText || "Your reservation could not be added. Please try again."));
        window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        if (!this.isConnected) return;
        status.textContent = error.name === 'AbortError' ? (this.dataset.presaleTimeoutText || "The request timed out. Please check your cart before trying again.") : error.message;
        button.disabled = !this.remaining();
      } finally {
        clearTimeout(timeout);
        this.busy = false;
        button.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('presale-page', PresalePage);
})();
